import classNames from "classnames";
import { useTranslation } from "react-i18next";
import type { RootState } from "~/redux/store";
import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";
import { fetchTopsellerProductsThunk } from "~/redux/products/topsellerProducts/actions";
import {
  ProductRecommendationSection,
  ProductRecommendationTitle,
  ProductRecoType,
} from "~/feature/SaleRecommendations/components/ProductRecommendationSection/ProductRecommendationSection";
import { type ProductData } from "~/models/ProductData";

export interface TopsellerRecommendationsProps {
  className?: string;
}

/**
 * Search page topseller recommendations, aka "Customers often buy"
 */
export const SRPTopsellerRecommendations = (props: TopsellerRecommendationsProps) => {
  return <TopsellerRecommendations {...props} />;
};

export const TopsellerRecommendations = ({ className }: TopsellerRecommendationsProps) => {
  const { t } = useTranslation();

  const { loading, products } = useSRPTopsellerProducts();
  if (!loading && !products.length) {
    return null;
  }

  const getTrackingAttributes = (product: ProductData) => {
    const rootTrackingAttributes = {
      [`tracking-aux-cob-product-parent-marker`]: "true",
      [`tracking-aux-cob-is-campaign-belonging`]: "false",
      [`tracking-aux-cob-campaign`]: "None",
    };
    const linkProps = {
      [`tracking-aux-cob-product-name`]: product.designation,
      [`tracking-aux-cob-product-id`]: product.sapMaterialNumber,
      target: "_blank",
    };
    const buttonProps = {
      [`tracking-aux-cob-add-to-cart-button`]: "true",
      reco_type: "cob",
    };
    const gaLocation = "Topseller recommendations SRP";
    return { rootTrackingAttributes, linkProps, buttonProps, gaLocation };
  };

  return (
    <ProductRecommendationSection
      className={classNames(className)}
      title={<ProductRecommendationTitle>{t("page.srp.topseller.title")}</ProductRecommendationTitle>}
      loading={loading}
      loadingLabel={t("feature.topseller.loadingSection")}
      products={products}
      variant={ProductRecoType.topSeller}
      getTrackingAttributes={getTrackingAttributes}
    />
  );
};

function useSRPTopsellerProducts() {
  const productCode = useSelector((state: RootState) => state.srp.firstResultProductCode);
  const data = useSelector((state: RootState) => (productCode ? state.products.topsellerProducts[productCode] : null));
  const dispatch = useDispatch();

  useEffect(() => {
    if (productCode) {
      dispatch(fetchTopsellerProductsThunk(productCode));
    }
  }, [productCode]);

  return {
    loading: !data || data?.state === "loading",
    products: data?.data?.slice(0, 4) ?? [], // As per SCHAEFF-33361, Max 4 products
  };
}
